import React, { useEffect, useMemo, useRef, useState } from 'react';

import type { SerializedStyles } from '@emotion/react';
import type { MultiSelectOption } from '@rtkwlf/fenrir-react';
import { MultiSelect, Primitive } from '@rtkwlf/fenrir-react';
import { useTranslation } from 'react-i18next';

import { usePrevious } from '@packages/hooks/usePrevious';
import { createOption } from '@packages/utils/create-options/create-options';

import type { ItemType as TempType } from './SelectElements';

export type ItemType = TempType;

export interface MultiSelectorProps {
  /** List of items to be displayed as a Dropdown Select Input */
  items: Array<ItemType>;

  /** Optional Title for the Select input */
  title?: string;
  titleKey?: string;

  /** Optional placeholder text */
  placeholder?: string;

  /** EmotionJS based css style */
  style?: SerializedStyles;

  handleSelectedItemsChange?: (optionsSelected: MultiSelectOption[]) => void;

  handleFilterChange?: (newFilter: string) => void;

  /** Update this value to reset unselect or reset the Select Input.
   * This is a way to reset the Select from its parent */
  resetCounter?: number;

  /** An initial array of items that are considered to be selected */
  initialSelectedItems?: ItemType[];

  /** Loading state to indicate the fetching of Type Ahead filters data */
  isLoadingTypeAhead?: boolean;

  /** Error state to indicate the fetching of Type Ahead filters data */
  isErrorTypeAhead?: boolean;
}

export const MultiSelector: React.FC<MultiSelectorProps> = ({
  title,
  titleKey,
  style,
  items,
  placeholder,
  resetCounter = 0,
  initialSelectedItems = [],
  handleSelectedItemsChange,
  handleFilterChange,
  isLoadingTypeAhead,
  isErrorTypeAhead,
}) => {
  const [selectedItems, setSelectedItems] = useState(initialSelectedItems);
  const { t } = useTranslation();
  const titleText = useMemo(
    () => (titleKey ? t(titleKey) : title?.replace(/_/g, ' ')),
    [titleKey, title, t],
  );

  const initialLoad = useRef(true);
  const prevResetCounter = usePrevious(resetCounter);
  useEffect(() => {
    // This prevents MultiSelect to reset the initial selected values.
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }

    if (resetCounter !== prevResetCounter) {
      setSelectedItems([]);
    }
  }, [resetCounter, setSelectedItems, prevResetCounter, initialSelectedItems]);

  const prevInitialSelectedItems = usePrevious(initialSelectedItems);
  useEffect(() => {
    if (
      prevInitialSelectedItems &&
      // Note - JSON.stringify is needed to do deep comparison. Otherwise, it goes in a loop.
      JSON.stringify(prevInitialSelectedItems) !==
        JSON.stringify(initialSelectedItems) &&
      JSON.stringify(selectedItems) !== JSON.stringify(initialSelectedItems)
    ) {
      setSelectedItems(initialSelectedItems);
    }
  }, [
    prevInitialSelectedItems,
    setSelectedItems,
    initialSelectedItems,
    selectedItems,
  ]);

  return (
    <Primitive.div css={style} position="relative">
      {titleText && (
        <Primitive.div
          fontSize="small"
          fontWeight="bold"
          paddingBottom="xsmall"
          textColor="body"
          textTransform="capitalize"
        >
          {titleText}
        </Primitive.div>
      )}
      <MultiSelect
        options={items.map(({ value, label }) =>
          createOption(value, label ?? ''),
        )}
        selectedOptions={selectedItems.map(({ value, label }) =>
          createOption(value, label ?? ''),
        )}
        placeholder={placeholder}
        emptyText={
          isLoadingTypeAhead
            ? t('ui:multiSelect.loading')
            : isErrorTypeAhead
              ? t('ui:multiSelect.error')
              : t('ui:multiSelect.noData')
        }
        onSelectedOptionsChange={(changes) => {
          setSelectedItems(changes);
          handleSelectedItemsChange?.(changes);
        }}
        onFilterChange={handleFilterChange}
      />
    </Primitive.div>
  );
};
